// src/components/common/mini-rte/MiniRTE.tsx
"use client";

import React, { useEffect, useMemo, useRef } from "react";
import { useField } from "formik";
import { Bold, Italic, Underline as UnderlineIcon, CornerDownLeft, Pilcrow } from "lucide-react";
import { Textarea, Button } from "../../common";
import { isRTLLanguage, isRTLText } from "../../../utils/helpers/languageHelpers";
import {
  mdLiteToPreviewHtml,
  mdLiteToHtml,
  htmlToMdLite,
  ensureBlocks,
  exec, // italic/underline/paragraph helpers from utils
} from "../../../utils/text/mdLite";
import "./MiniRTE.css";

type VisualMode = "contenteditable" | "overlay";

interface MiniRTEProps {
  name: string;
  label?: string;
  maxLength?: number;      // legacy (overlay)
  maxChars?: number;       // soft limit for CE mode
  showCounter?: boolean;   // default: false (BC-safe)
  dir?: "rtl" | "ltr";
  langCode?: string;
  placeholder?: string;
  visualMode?: VisualMode;
}

/* ========================= Bold helpers (DOM-normalized toggle) ========================= */

// Convert scattered bolds (<b>, <span style="font-weight:...">) into <strong data-lit="1">
function normalizeBoldTags(root: HTMLElement) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT, null);
  const toStrong: HTMLElement[] = [];
  while (walker.nextNode()) {
    const el = walker.currentNode as HTMLElement;

    // <b> → <strong data-lit="1">
    if (el.tagName === "B") toStrong.push(el);

    // <span style="font-weight:..."> → <strong data-lit="1"> if ≥ 600/bold
    if (el.tagName === "SPAN") {
      const fw = (el.style && el.style.fontWeight) || "";
      const isNum = /^\d+$/.test(fw);
      if (fw === "bold" || (isNum && parseInt(fw, 10) >= 600)) toStrong.push(el);
    }
  }

  for (const el of toStrong) {
    const strong = document.createElement("strong");
    strong.setAttribute("data-lit", "1");
    while (el.firstChild) strong.appendChild(el.firstChild);
    el.parentNode?.replaceChild(strong, el);
  }
}

// Remove inline font-weight to avoid confusing the toggle
function stripInlineFontWeight(root: HTMLElement) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT, null);
  while (walker.nextNode()) {
    const el = walker.currentNode as HTMLElement;
    if (el.style && el.style.fontWeight) el.style.fontWeight = "";
  }
}

// Merge adjacent <strong> and flatten nested ones
function mergeAndDedupStrong(root: HTMLElement) {
  // Flatten nested: <strong><strong>t</strong></strong> → <strong>t</strong>
  const nested = root.querySelectorAll("strong strong");
  nested.forEach((inner) => {
    const parent = inner.parentElement!;
    while (inner.firstChild) parent.insertBefore(inner.firstChild, inner);
    inner.remove();
  });

  // Merge siblings: <strong>a</strong><strong>b</strong> → <strong>ab</strong>
  const all = Array.from(root.querySelectorAll("strong"));
  for (let i = 0; i < all.length - 1; i++) {
    const a = all[i];
    const b = all[i + 1];
    if (a.nextSibling === b) {
      while (b.firstChild) a.appendChild(b.firstChild);
      b.remove();
    }
  }

  // Ensure the canonical marker
  root.querySelectorAll("strong").forEach((s) => s.setAttribute("data-lit", "1"));
}

// Toggle bold using execCommand with pre/post DOM normalization
function toggleBoldNormalized(root: HTMLElement) {
  // pre-normalize
  stripInlineFontWeight(root);
  normalizeBoldTags(root);
  mergeAndDedupStrong(root);

  // native toggle (handles selection boundaries best)
  document.execCommand("bold", false);

  // post-normalize
  stripInlineFontWeight(root);
  normalizeBoldTags(root);
  mergeAndDedupStrong(root);
}

/* =================================================================== */

export default function MiniRTE({
  name,
  label,
  maxLength,
  maxChars,
  showCounter = false,
  dir,
  langCode,
  placeholder,
  visualMode = "contenteditable",
}: MiniRTEProps) {
  const [field, , helpers] = useField<string>(name);
  const taRef = useRef<HTMLTextAreaElement | null>(null);
  const previewRef = useRef<HTMLDivElement | null>(null);
  const ceRef = useRef<HTMLDivElement | null>(null);

  // prevent model↔DOM ping-pong
  const lastMdRef = useRef<string | null>(null);
  const suppressModelSyncRef = useRef(false);

  // resolve direction
  const resolvedDir: "rtl" | "ltr" = useMemo(() => {
    if (dir) return dir;
    if (langCode) return isRTLLanguage(langCode) ? "rtl" : "ltr";
    return isRTLText(field.value || "") ? "rtl" : "ltr";
  }, [dir, langCode, field.value]);

  // shared counter
  const currentLen = (field.value || "").length;
  const limit = maxChars ?? maxLength;

  /* ---------- MODE A: contenteditable ---------- */

  // model → DOM (only on external changes)
  useEffect(() => {
    if (visualMode !== "contenteditable" || !ceRef.current) return;
    if (suppressModelSyncRef.current) return;
    const html = ensureBlocks(mdLiteToHtml(field.value || ""));
    const shouldWrite =
      lastMdRef.current === null ||
      field.value !== lastMdRef.current ||
      ceRef.current.innerHTML !== html;
    if (shouldWrite) {
      ceRef.current.innerHTML = html;
      lastMdRef.current = field.value || "";
    }
  }, [field.value, visualMode]);

  // DOM → model
  const onCeInput = () => {
    if (visualMode !== "contenteditable" || !ceRef.current) return;
    suppressModelSyncRef.current = true;

    // sanitize DOM before reading
    stripInlineFontWeight(ceRef.current);
    normalizeBoldTags(ceRef.current);
    mergeAndDedupStrong(ceRef.current);

    const md = htmlToMdLite(ceRef.current.innerHTML);
    const clipped = typeof maxChars === "number" ? md.slice(0, maxChars) : md;

    if (clipped !== field.value) {
      helpers.setValue(clipped);
      lastMdRef.current = clipped;
    }

    // re-clamp DOM if truncated
    if (clipped.length !== md.length) {
      const html = ensureBlocks(mdLiteToHtml(clipped));
      ceRef.current.innerHTML = html;
    }

    Promise.resolve().then(() => {
      suppressModelSyncRef.current = false;
    });
  };

  // keyboard handlers (CE)
  const onCeKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (visualMode !== "contenteditable") return;
    const mod = e.metaKey || e.ctrlKey;

    // Bold (with normalization)
    if (mod && e.key.toLowerCase() === "b") {
      e.preventDefault();
      if (ceRef.current) {
        toggleBoldNormalized(ceRef.current);
        onCeInput(); // sync to model
      }
      return;
    }

    // Italic
    if (mod && e.key.toLowerCase() === "i") {
      e.preventDefault();
      exec("italic");
      onCeInput();
      return;
    }
    // Enter: native behavior
  };

  // toolbar actions
  const onToolbarBold = () => {
    if (ceRef.current) {
      toggleBoldNormalized(ceRef.current);
      onCeInput();
    }
  };
  const onToolbarItalic = () => { exec("italic"); onCeInput(); };
  const onToolbarUnderline = () => { exec("underline"); onCeInput(); };
  const onToolbarLine = () => { exec("insertLineBreak"); onCeInput(); };
  const onToolbarParagraph = () => { exec("insertParagraph"); onCeInput(); };

  // paste as plain text
  const onCePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text/plain");
    document.execCommand("insertText", false, text);
    onCeInput();
  };

  /* ---------- MODE B: overlay (legacy) ---------- */

  // textarea/preview scroll sync
  useEffect(() => {
    if (visualMode !== "overlay" || !taRef.current || !previewRef.current) return;
    const ta = taRef.current, pv = previewRef.current;
    const sync = () => { pv.scrollTop = ta.scrollTop; pv.scrollLeft = ta.scrollLeft; };
    ta.addEventListener("scroll", sync, { passive: true });
    return () => ta.removeEventListener("scroll", sync);
  }, [visualMode]);

  // keyboard handlers (overlay)
  const onKeyDownTA = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const mod = e.metaKey || e.ctrlKey;
    if (!taRef.current) return;

    if (mod && e.key.toLowerCase() === "b") {
      e.preventDefault();
      const { selectionStart, selectionEnd, value } = taRef.current;
      const before = value.slice(0, selectionStart);
      const sel = value.slice(selectionStart, selectionEnd);
      const after = value.slice(selectionEnd);
      const next = `${before}**${sel}**${after}`;
      helpers.setValue(next);
      requestAnimationFrame(() => taRef.current?.setSelectionRange(selectionStart + 2, selectionEnd + 2));
    }

    if (mod && e.key.toLowerCase() === "i") {
      e.preventDefault();
      const { selectionStart, selectionEnd, value } = taRef.current;
      const before = value.slice(0, selectionStart);
      const sel = value.slice(selectionStart, selectionEnd);
      const after = value.slice(selectionEnd);
      const next = `${before}*${sel}*${after}`;
      helpers.setValue(next);
      requestAnimationFrame(() => taRef.current?.setSelectionRange(selectionStart + 1, selectionEnd + 1));
    }
  };

  return (
    <div className={`mini-rte dir-${resolvedDir}`}>

      {/* contenteditable mode */}
      {visualMode === "contenteditable" && (
        <div className="mini-rte-ce-wrapper">
          {/* hidden textarea to keep Formik wiring */}
          <Textarea
            {...field}
            name={name}
            label={label}
            maxLength={limit}
            style={{ display: "none" }}
            showCounter={false}
          />

          {/* toolbar */}
          <div className="mini-rte-toolbar">
            <Button variant="ghost" size="sm" type="button" onClick={onToolbarBold} aria-label="Bold">
              <Bold size={16} />
            </Button>
            <Button variant="ghost" size="sm" type="button" onClick={onToolbarItalic} aria-label="Italic">
              <Italic size={16} />
            </Button>
            <Button variant="ghost" size="sm" type="button" onClick={onToolbarUnderline} aria-label="Underline">
              <UnderlineIcon size={16} />
            </Button>
            <Button variant="ghost" size="sm" type="button" onClick={onToolbarLine} aria-label="Line break">
              <CornerDownLeft size={16} />
            </Button>
            <Button variant="ghost" size="sm" type="button" onClick={onToolbarParagraph} aria-label="New paragraph">
              <Pilcrow size={16} />
            </Button>
          </div>

          <div
            ref={ceRef}
            className="mini-rte-ce"
            contentEditable
            role="textbox"
            aria-multiline="true"
            dir={resolvedDir}
            data-placeholder={placeholder || ""}
            onInput={onCeInput}
            onKeyDown={onCeKeyDown}
            onPaste={onCePaste}
            aria-label={label || name}
            aria-describedby={showCounter && limit ? `${name}-counter` : undefined}
          />

          {/* counter */}
          {showCounter && typeof limit === "number" && (
            <span id={`${name}-counter`} className="textarea-counter mini-rte-counter">
              {currentLen}/{limit}
            </span>
          )}
        </div>
      )}

      {/* overlay mode (legacy) */}
      {visualMode === "overlay" && (
        <div className="mini-rte-stack has-preview">
          <div
            ref={previewRef}
            className="mini-rte-preview"
            dir={resolvedDir}
            dangerouslySetInnerHTML={{
              __html:
                !field.value || field.value.length === 0
                  ? `<span class="mini-rte-placeholder">${placeholder ?? ""}</span>`
                  : mdLiteToPreviewHtml(field.value || ""),
            }}
          />
          <Textarea
            {...field}
            name={name}
            label={label}
            maxLength={limit}
            placeholder={undefined}
            dir={resolvedDir}
            onKeyDown={onKeyDownTA}
            ref={taRef as any}
            className="mini-rte-textarea-overlay"
            showCounter={showCounter}
          />
        </div>
      )}
    </div>
  );
}
